import React, { useState, useEffect } from "react";
import { JsonRpcProvider, Contract } from "ethers";
import { currentNetwork } from "../../utils/customNetwork";
import { getAuthReq } from "../../utils/apiHandlers";
import Spinner from "../../components/loaders/Spinner";

import {
  DrawHeader,
  DrawTabs,
  PrizeTable,
  CouponTabs,
  CouponTable,
} from "../../components";

const STORAGE_KEYS = {
  INITIALIZED: "mst_coupon_initialized",
  COUPONS: "mst_coupon_pool",
  ALL_COUPONS: "mst_coupon_all",
  CLICK_INDEX: "mst_coupon_click_index",
  WINNERS: "mst_coupon_winners",
  TRANSACTIONS: "mst_coupon_transactions",
};

const getDrawState = () => {
  let isInitialized = localStorage.getItem(STORAGE_KEYS.INITIALIZED) === "true";

  if (!isInitialized) {
    const coupons = Array.from({ length: 4000 }, (_, i) => `MST-${1001 + i}`);

    localStorage.setItem(STORAGE_KEYS.INITIALIZED, "true");
    localStorage.setItem(STORAGE_KEYS.COUPONS, JSON.stringify(coupons));
    localStorage.setItem(STORAGE_KEYS.ALL_COUPONS, JSON.stringify(coupons));
    localStorage.setItem(STORAGE_KEYS.CLICK_INDEX, "1");
    localStorage.setItem(STORAGE_KEYS.WINNERS, JSON.stringify({}));

    const chars = "0123456789abcdef";
    let hash = "0x";
    for (let i = 0; i < 64; i++) {
      hash += chars[Math.floor(Math.random() * chars.length)];
    }
    const initTx = {
      txHash: hash,
      clickIndex: 0,
      tierName: "Ledger Initialization",
      winnersDrawn: 0,
      timestamp: new Date().toISOString(),
      gasUsed: 421000,
    };
    localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify([initTx]));
    isInitialized = true;
  }

  const couponsRaw = localStorage.getItem(STORAGE_KEYS.COUPONS);
  const allCouponsRaw = localStorage.getItem(STORAGE_KEYS.ALL_COUPONS);
  const clickIndexRaw = localStorage.getItem(STORAGE_KEYS.CLICK_INDEX);
  const winnersRaw = localStorage.getItem(STORAGE_KEYS.WINNERS);
  const transactionsRaw = localStorage.getItem(STORAGE_KEYS.TRANSACTIONS);

  return {
    isInitialized,
    coupons: couponsRaw ? JSON.parse(couponsRaw) : [],
    allCoupons: allCouponsRaw ? JSON.parse(allCouponsRaw) : [],
    currentClickIndex: clickIndexRaw ? parseInt(clickIndexRaw, 10) : 1,
    winners: winnersRaw ? JSON.parse(winnersRaw) : {},
    transactions: transactionsRaw ? JSON.parse(transactionsRaw) : [],
  };
};

const DrawRewardDetails = () => {
  const [activeTab, setActiveTab] = useState("prize");
  const [couponType, setCouponType] = useState("selected");
  const [drawState, setDrawState] = useState(getDrawState);
  const [blockchainWinners, setBlockchainWinners] = useState(null);
  const [drawData, setDrawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [userCoupons, setUserCoupons] = useState([]);

  useEffect(() => {
    const syncState = () => {
      setDrawState(getDrawState());
    };

    window.addEventListener("storage", syncState);
    window.addEventListener("focus", syncState);

    // Initial sync
    syncState();

    return () => {
      window.removeEventListener("storage", syncState);
      window.removeEventListener("focus", syncState);
    };
  }, []);

  const fetchData = async (isInitial = false, showCardLoaders = false) => {
    if (isInitial) {
      setLoading(true);
    }
    if (showCardLoaders) {
      setIsFetching(true);
    }

    const contractAddress =
      process.env.REACT_APP_COUPON_DRAW_ADDRESS ||
      "0x904a0E4b35824b22F2eC7C4C8733224B27C1722F";
    const rpcUrl =
      currentNetwork?.rpcUrls?.default?.http?.[0] ||
      "https://testnetrpc.mstblockchain.com";
    const externalId = Number(process.env.REACT_APP_DRAW_EXTERNAL_ID || 4);

    try {
      let winnersArray = [];
      let draw = null;

      const abi = [
        "function externalToDrawId(uint256) view returns (uint256)",
        "function draws(uint256) view returns (uint256 id, uint256 externalId, uint256 totalCoupons, uint256 soldCoupons, uint256 maxWinners, uint256 winnersSelected, bool completed, bool exists)",
        "function getPositionSelectedCouponsByExternalId(uint256 externalId, uint256 start, uint256 end) view returns (string[])",
      ];

      if (window.ethers) {
        const provider = new window.ethers.providers.JsonRpcProvider(rpcUrl);
        const contract = new window.ethers.Contract(
          contractAddress,
          abi,
          provider,
        );

        const drawId = await contract.externalToDrawId(externalId);
        draw = await contract.draws(drawId);
        winnersArray = await contract.getPositionSelectedCouponsByExternalId(
          externalId,
          1,
          250,
        );
      } else {
        const provider = new JsonRpcProvider(rpcUrl);
        const contract = new Contract(contractAddress, abi, provider);

        const drawId = await contract.externalToDrawId(externalId);
        draw = await contract.draws(drawId);
        winnersArray = await contract.getPositionSelectedCouponsByExternalId(
          externalId,
          1,
          250,
        );
      }

      if (draw) {
        setDrawData(draw);
      }

      if (winnersArray && winnersArray.length > 0) {
        const mappedWinners = {
          12: [winnersArray[0]].filter((w) => w && w !== ""),
          11: [winnersArray[1]].filter((w) => w && w !== ""),
          10: [winnersArray[2]].filter((w) => w && w !== ""),
          9: [winnersArray[3]].filter((w) => w && w !== ""),
          8: [winnersArray[4]].filter((w) => w && w !== ""),
          7: [winnersArray[5]].filter((w) => w && w !== ""),
          6: [winnersArray[6]].filter((w) => w && w !== ""),
          5: winnersArray.slice(7, 27).filter((w) => w && w !== ""),
          4: winnersArray.slice(27, 67).filter((w) => w && w !== ""),
          3: winnersArray.slice(67, 90).filter((w) => w && w !== ""),
          2: winnersArray.slice(90, 150).filter((w) => w && w !== ""),
          1: winnersArray.slice(150, 250).filter((w) => w && w !== ""),
        };

        const hasWinners = Object.values(mappedWinners).some(
          (arr) => arr.length > 0,
        );
        if (hasWinners) {
          setBlockchainWinners(mappedWinners);
        }
      }
    } catch (err) {
      console.error("Failed to fetch blockchain data:", err);
    } finally {
      if (isInitial) {
        setLoading(false);
      }
      setIsFetching(false);
    }
  };

  useEffect(() => {
    let active = true;

    const fetchUserCoupons = async () => {
      try {
        const res = await getAuthReq(
          "/coupons/my-coupons?take=5000&type=SelfPurchase",
        );
        if (active && res && res.status && res.data && res.data.items) {
          const codes = res.data.items.map((item) => item.code).filter(Boolean);
          setUserCoupons(codes);
        }
      } catch (err) {
        console.error("Failed to fetch user coupons:", err);
      }
    };

    fetchUserCoupons();
    fetchData(true, true);

    return () => {
      active = false;
    };
  }, []);

  const allCoupons =
    drawState.allCoupons && drawState.allCoupons.length > 0
      ? drawState.allCoupons
      : Array.from({ length: 4000 }, (_, i) => `MST-${1001 + i}`);
  const winners = blockchainWinners || drawState.winners;

  // Selected coupons
  const selectedCoupons = [];
  let idCounter = 1;

  const PRIZE_TIERS = [
    { clickIndex: 1, tierName: "151st–250th", amount: "1 Fraction" },
    { clickIndex: 2, tierName: "91st–150th", amount: "2 Fraction" },
    { clickIndex: 3, tierName: "68th–90th", amount: "3 Fraction" },
    { clickIndex: 4, tierName: "28th–67th", amount: "₹7,827.50" },
    { clickIndex: 5, tierName: "8th–27th", amount: "₹15,655" },
    { clickIndex: 6, tierName: "7th", amount: "₹31,310" },
    { clickIndex: 7, tierName: "6th", amount: "₹62,620" },
    { clickIndex: 8, tierName: "5th", amount: "₹93,930" },
    { clickIndex: 9, tierName: "4th", amount: "₹1,56,550" },
    { clickIndex: 10, tierName: "3rd", amount: "₹3,13,100" },
    { clickIndex: 11, tierName: "2nd", amount: "₹4,69,650" },
    { clickIndex: 12, tierName: "1st", amount: "₹6,26,200 (Grand Prize)" },
  ];

  const sortedClickIndices = Object.keys(winners)
    .map(Number)
    .sort((a, b) => a - b);

  sortedClickIndices.forEach((clickIndex) => {
    const couponList = winners[clickIndex] || [];
    const tier = PRIZE_TIERS.find((t) => t.clickIndex === clickIndex);
    couponList.forEach((couponCode) => {
      selectedCoupons.push({
        id: idCounter++,
        name: `User ${couponCode}`,
        coupon: couponCode,
        tierName: tier ? tier.tierName : "Consolation",
      });
    });
  });

  // Rejected coupons
  const selectedSet = new Set(selectedCoupons.map((c) => c.coupon));
  const rejectedCoupons = [];
  let rejIdCounter = 1;
  allCoupons.forEach((couponCode) => {
    if (!selectedSet.has(couponCode)) {
      rejectedCoupons.push({
        id: rejIdCounter++,
        name: `User ${couponCode}`,
        coupon: couponCode,
      });
    }
  });

  const totalCount = allCoupons.length;
  const selectedCount = selectedCoupons.length;
  const rejectedCount = Math.max(0, totalCount - selectedCount);

  const contractAddress =
    process.env.REACT_APP_COUPON_DRAW_ADDRESS ||
    "0x904a0E4b35824b22F2eC7C4C8733224B27C1722F";
  const contractLink =
    (process.env.REACT_APP_ADDRESS_URL ||
      "https://testnet.mstscan.com/address/") + contractAddress;

  const drawDetails = {
    drawName: `Lucky Draw #${process.env.REACT_APP_DRAW_EXTERNAL_ID || 2}`,
    contractAddress,
    contractLink,
    total: totalCount,
    selected: selectedCount,
    rejected: rejectedCount,
  };

  const prizeData = [
    {
      rank: "1st",
      amount: "₹6,26,200",
      coupon: winners[12]?.[0] || "Awaiting Draw",
    },
    {
      rank: "2nd",
      amount: "₹4,69,650",
      coupon: winners[11]?.[0] || "Awaiting Draw",
    },
    {
      rank: "3rd",
      amount: "₹3,13,100",
      coupon: winners[10]?.[0] || "Awaiting Draw",
    },
    {
      rank: "4th",
      amount: "₹1,56,550",
      coupon: winners[9]?.[0] || "Awaiting Draw",
    },
    {
      rank: "5th",
      amount: "₹93,930",
      coupon: winners[8]?.[0] || "Awaiting Draw",
    },
    {
      rank: "6th",
      amount: "₹62,620",
      coupon: winners[7]?.[0] || "Awaiting Draw",
    },
    {
      rank: "7th",
      amount: "₹31,310",
      coupon: winners[6]?.[0] || "Awaiting Draw",
    },
  ];

  const dynamicPrizeGroups = [
    {
      rank: "8th - 27th",
      amount: "₹15,655",
      winners: Array.from({ length: 20 }, (_, i) => ({
        rank: i + 8,
        coupon: winners[5]?.[i] || "Awaiting Draw",
        amount: "₹15,655",
      })),
    },
    {
      rank: "28th - 67th",
      amount: "₹7,827.50",
      winners: Array.from({ length: 40 }, (_, i) => ({
        rank: i + 28,
        coupon: winners[4]?.[i] || "Awaiting Draw",
        amount: "₹7,827.50",
      })),
    },
    {
      rank: "68th - 90th",
      amount: "3 Fraction",
      winners: Array.from({ length: 23 }, (_, i) => ({
        rank: i + 68,
        coupon: winners[3]?.[i] || "Awaiting Draw",
        amount: "3 Fraction",
      })),
    },
    {
      rank: "91st - 150th",
      amount: "2 Fraction",
      winners: Array.from({ length: 60 }, (_, i) => ({
        rank: i + 91,
        coupon: winners[2]?.[i] || "Awaiting Draw",
        amount: "2 Fraction",
      })),
    },
    {
      rank: "151st - 250th",
      amount: "1 Fraction",
      winners: Array.from({ length: 100 }, (_, i) => ({
        rank: i + 151,
        coupon: winners[1]?.[i] || "Awaiting Draw",
        amount: "1 Fraction",
      })),
    },
  ];

  // Limit rejected coupons to first 200 items for UI responsiveness
  const displayedRejectedCoupons = rejectedCoupons.slice(0, 200);

  return (
    <div className="w-full">
      {loading && <Spinner />}
      <DrawHeader
        drawDetails={{ ...drawDetails, drawData }}
        loading={loading || isFetching}
        onRefresh={() => fetchData(true, true)}
      />

      <DrawTabs activeTab={activeTab} setActiveTab={setActiveTab} />

      {activeTab === "prize" && (
        <PrizeTable
          data={prizeData}
          groups={dynamicPrizeGroups}
          userCoupons={userCoupons}
        />
      )}

      {activeTab === "coupons" && (
        <div>
          <CouponTabs couponType={couponType} setCouponType={setCouponType} />
          {couponType === "rejected" && rejectedCoupons.length > 200 && (
            <p className="text-gray-400 text-sm mb-3">
              Showing first 200 unselected coupons out of {rejectedCount} total.
            </p>
          )}
          <CouponTable
            data={
              couponType === "selected"
                ? selectedCoupons
                : displayedRejectedCoupons
            }
            couponType={couponType}
            userCoupons={userCoupons}
          />
        </div>
      )}
    </div>
  );
};

export default DrawRewardDetails;
